import React, {useState} from 'react';
import axios from 'axios';
import clsx from 'clsx';
import {enqueueSnackbar} from 'notistack';
import {KeenIcon} from '@/components';
import {Characteristic, Importance, SkillLevel} from '@/pages/buddy/types/Characteristic';
import CharacteristicBadge from "@/components/badges/CharacteristicBadge.tsx";

interface EditableListProps {
    title: string;
    items: Characteristic[];
    // entity is either "job" or "candidate"
    entity: "job" | "candidate";
    // entityId holds the identifier: either job_p_id or candidate_p_id
    entityId: string;
    // endpoint for updating characteristics (e.g., 'required-skills', 'frameworks', 'technologies' or 'skills')
    endpoint: string;
    className?: string;
}

const EditableList: React.FC<EditableListProps> = ({
                                                       title,
                                                       items,
                                                       entity,
                                                       entityId,
                                                       endpoint,
                                                       className,
                                                   }) => {
    // For jobs, ensure every characteristic has importance; default to "Optional" if missing.
    const normalizedItems =
        entity === "job"
            ? items.map((char) => ({
                ...char,
                importance: char.importance ? char.importance : "Optional",
            }))
            : items;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedItems, setEditedItems] = useState<Characteristic[]>([...normalizedItems]);
    const [loading, setLoading] = useState<boolean>(false);

    // For new characteristic – in job mode, include importance; in candidate mode, ignore importance.
    const initialChar: Characteristic =
        entity === "job"
            ? {name: '', importance: "Optional", level: "Novice"}
            : {name: '', level: "Novice"} as Characteristic;
    const [newChar, setNewChar] = useState<Characteristic>(initialChar);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    const handleEditClick = () => setIsEditing(true);

    const handleCancelClick = () => {
        setEditedItems([...normalizedItems]);
        setNewChar(initialChar);
        setIsEditing(false);
    };

    const getPlaceholderLabel = () => {
        const placeholderMap: Record<string, string> = {
            "required-skills": "Skill Name",
            "skills": "Skill Name",
            "frameworks": "Framework Name",
            "technologies": "Technology Name",
            "spoken-languages": "Language Name",
            "certifications": "Certificate Name",
        };
        return placeholderMap[endpoint] || "Characteristic Name";
    };

    const handleSaveClick = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const url = `${API_URL}/${entity}/${entityId}/${endpoint}`;
            const payload =
                entity === "job"
                    ? {characteristics: editedItems}
                    : {
                        characteristics: editedItems.map(item => ({name: item.name, level: item.level})),
                    };
            await axios.put(url, payload, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setIsEditing(false);
        } catch (err: any) {
            enqueueSnackbar(`Failed to update ${title.toLowerCase()}: ${err}`, {
                variant: 'solid',
                state: 'danger',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = (index: number) => {
        const newList = [...editedItems];
        newList.splice(index, 1);
        setEditedItems(newList);
    };

    const handleCharChange = (index: number, field: keyof Characteristic, value: any) => {
        const newList = [...editedItems];
        // For candidates, ignore changes to 'importance'
        if (entity === "candidate" && field === "importance") return;
        newList[index] = {...newList[index], [field]: value};
        setEditedItems(newList);
    };

    const handleNewCharChange = (field: keyof Characteristic, value: any) => {
        if (entity === "candidate" && field === "importance") return;
        setNewChar({...newChar, [field]: value});
    };

    const handleAddNewChar = () => {
        if (newChar.name.trim() === '') return;
        const duplicate = editedItems.some(
            (item) => item.name.trim().toLowerCase() === newChar.name.trim().toLowerCase()
        );
        if (duplicate) {
            enqueueSnackbar(`Duplicate ${entity === "job" ? "characteristic" : "skill"}: ${newChar.name}`, {variant: 'warning'});
            return;
        }
        setEditedItems([...editedItems, {...newChar, name: newChar.name.trim()}]);
        setNewChar(initialChar);
    };

    // Sorting function:
    const sortByAttributes = (items: Characteristic[]): Characteristic[] => {
        if (entity === "job") {
            const importanceOrder: Importance[] = ["Essential", "Desirable", "Optional", "Unspecified"];
            const levelOrder: SkillLevel[] = ["Expert", "Professional", "Intermediate", "Amateur", "Novice", "Unspecified"];
            return items.slice().sort((a, b) => {
                const impDiff = importanceOrder.indexOf(a.importance) - importanceOrder.indexOf(b.importance);
                if (impDiff !== 0) return impDiff;
                return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
            });
        } else {
            const levelOrder: SkillLevel[] = ["Expert", "Professional", "Intermediate", "Amateur", "Novice", "Unspecified"];
            return items.slice().sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level));
        }
    };

    const sortedItems = sortByAttributes(editedItems);

    const renderEditItem = (item: Characteristic, index: number) => (
        <div key={index} className="flex flex-col mb-4">
            <div className="flex items-center gap-2.5 w-full">
                <input
                    type="text"
                    value={item.name}
                    onChange={(e) => handleCharChange(index, 'name', e.target.value)}
                    placeholder={getPlaceholderLabel()}
                    className="input input-bordered input-sm flex-1"
                />
                <button
                    type="button"
                    className="btn btn-ghost btn-sm text-red-500"
                    onClick={() => handleDeleteItem(index)}
                    aria-label={`Delete ${item.name}`}
                >
                    &times;
                </button>
            </div>
            <div className="flex items-center gap-2.5 mt-2">
                {entity === "job" && (
                    <select
                        value={item.importance}
                        onChange={(e) => handleCharChange(index, 'importance', e.target.value)}
                        className="select select-bordered select-sm w-32"
                    >
                        <option value="Unspecified">Unspecified</option>
                        <option value="Optional">Optional</option>
                        <option value="Desirable">Desirable</option>
                        <option value="Essential">Essential</option>
                    </select>
                )}
                <select
                    value={item.level}
                    onChange={(e) => handleCharChange(index, 'level', e.target.value)}
                    className="select select-bordered select-sm w-32"
                >
                    <option value="Unspecified">Unspecified</option>
                    <option value="Novice">Novice</option>
                    <option value="Amateur">Amateur</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Professional">Professional</option>
                    <option value="Expert">Expert</option>
                </select>
            </div>
        </div>
    );

    const renderNewCharacteristicRow = () => (
        <div className="flex flex-col mt-2 mb-4">
            <h4 className="text-sm font-medium mb-2">Add new {title}</h4>
            <div className="flex flex-col mb-2">
                <div className="flex items-center gap-2.5 w-full">
                    <input
                        type="text"
                        value={newChar.name}
                        onChange={(e) => handleNewCharChange('name', e.target.value)}
                        placeholder={getPlaceholderLabel()}
                        className="input input-bordered input-sm flex-1"
                    />
                </div>
                <div className="flex items-center gap-2.5 mt-2">
                    {entity === "job" && (
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Importance</span>
                            <select
                                value={newChar.importance}
                                onChange={(e) => handleNewCharChange('importance', e.target.value)}
                                className="select select-bordered select-sm w-32"
                            >
                                <option value="Unspecified">Unspecified</option>
                                <option value="Optional">Optional</option>
                                <option value="Desirable">Desirable</option>
                                <option value="Essential">Essential</option>
                            </select>
                        </div>
                    )}
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Level</span>
                        <select
                            value={newChar.level}
                            onChange={(e) => handleNewCharChange('level', e.target.value)}
                            className="select select-bordered select-sm w-32"
                        >
                            <option value="Unspecified">Unspecified</option>
                            <option value="Novice">Novice</option>
                            <option value="Amateur">Amateur</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Professional">Professional</option>
                            <option value="Expert">Expert</option>
                        </select>
                    </div>
                </div>
            </div>
            <div className="flex items-center">
                <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleAddNewChar}
                    disabled={!newChar.name.trim()}
                >
                    <KeenIcon icon="add-notepad"/>
                    Add
                </button>
            </div>
        </div>
    );

    const renderViewItem = (item: Characteristic, index: number) => {
        if (entity === "job") {
            return <CharacteristicBadge key={index} entity="job" characteristicJob={item}/>;
        } else if (entity === "candidate") {
            return <CharacteristicBadge key={index} entity="candidate" characteristicCandidate={item}/>;
        }
        return null; // fallback in case neither
    };

    return (
        <div className={clsx('card', className)}>
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit"/>
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button className="btn btn-primary btn-sm" onClick={handleSaveClick} disabled={loading}>
                            <KeenIcon icon="file-up"/>
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelClick} disabled={loading}>
                            <KeenIcon icon="tablet-delete"/>
                            Cancel
                        </button>
                    </div>
                )}
            </div>
            <div className="card-body">
                {isEditing ? (
                    <>
                        <div className="flex flex-col gap-2.5">
                            {editedItems.map((item, index) => renderEditItem(item, index))}
                        </div>
                        {renderNewCharacteristicRow()}
                    </>
                ) : (
                    <div className="flex flex-wrap gap-2.5">
                        {sortedItems.map((item, index) => renderViewItem(item, index))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditableList;
